import type { ImageType } from '@/types/generics'
import type {
  ProposalYacht,
  ProposalYachtPageDetails,
  ProposalYachtPricingOverride,
  PurchaseType,
  TimeframeType,
} from '@/types/proposals'
import type {
  PriceFrequency,
  SalesforceCharterPrice,
  SalesforceSalePrice,
} from '@/types/sanity'
import type { YachtPricing } from '@/types/yachts'

import { getDMYDate } from '@/utils/helpers/format-date'
import { parseMediaObject } from '@/utils/media-transformer'
import { getSeasonOrderList } from '@/utils/pricing'

import { PLACEHOLDER_YACHT_CARD_IMAGE } from '@/constants/content'

const CHARTER_PRICING_TYPE = 'charter'
const SALE_PRICING_TYPE = 'purchase'
const MIXED_PRICING_TYPE = 'mixed'

export const isValidPurchaseType = (
  purchaseType:
    | typeof CHARTER_PRICING_TYPE
    | typeof SALE_PRICING_TYPE
    | typeof MIXED_PRICING_TYPE,
) => {
  return [CHARTER_PRICING_TYPE, SALE_PRICING_TYPE, MIXED_PRICING_TYPE].includes(
    purchaseType,
  )
}

const mapSalesforceYachtCharterPrice = (
  allPrices: Array<YachtPricing> | [],
  price: SalesforceCharterPrice,
): Array<YachtPricing> | [] => {
  const priceYear =
    price && price.SF_Start_Date__c
      ? price.SF_Start_Date__c.split('-')[0]
      : null

  if (!price.SF_Low_Rate__c && !price.SF_High_Rate__c) return allPrices

  return [
    ...allPrices,
    {
      type: CHARTER_PRICING_TYPE,
      isOverride: false,
      season: price.SF_Season__c ?? null,
      currency: price.SF_CurrencyIsoCode,
      frequency: 'w', // SF Charter Price doesn't provide a frequency. Default to week
      availability: {
        ...(priceYear ? { year: priceYear } : {}),
        from: price.SF_Start_Date__c ?? null,
        until: price.SF_End_Date__c ?? null,
      },
      amount: {
        value: price.SF_High_Rate__c
          ? Number(price.SF_High_Rate__c)
          : Number(price.SF_Low_Rate__c),
        lowValue: price.SF_Low_Rate__c ? Number(price.SF_Low_Rate__c) : null,
        highValue: price.SF_High_Rate__c ? Number(price.SF_High_Rate__c) : null,
      },
    },
  ]
}

const mapSalesforceYachtSalePrice = (
  allPrices: Array<YachtPricing> | [],
  price: SalesforceSalePrice,
): Array<YachtPricing> | [] => {
  if (!price.SF_Current_Asking_Price__c) return allPrices

  return [
    ...allPrices,
    {
      type: SALE_PRICING_TYPE,
      isOverride: false,
      currency: price.SF_CurrencyIsoCode,
      amount: {
        value: Number(price.SF_Current_Asking_Price__c),
      },
    },
  ]
}

const mapOverrideYachtPrice = (
  price: ProposalYachtPricingOverride,
): YachtPricing => {
  const parsedType =
    price.yachtPricingSeason === SALE_PRICING_TYPE ||
    price.yachtPricingFrequency === SALE_PRICING_TYPE
      ? SALE_PRICING_TYPE
      : CHARTER_PRICING_TYPE

  return {
    type: parsedType,
    isOverride: true,
    isCardDisplayPrice: price.yachtPricingUseOnCard,
    season: price.yachtPricingSeason,
    currency: price.yachtPricingCurrency,
    frequency: price.yachtPricingFrequency,
    availability: {
      from: price.yachtPricingFrom,
      until: price.yachtPricingUntil,
    },
    amount: {
      value: Number(price.yachtPricingAmount),
      lowValue:
        parsedType === CHARTER_PRICING_TYPE
          ? Number(price.yachtPricingAmount)
          : null,
      highValue: null,
    },
  }
}

export const combineYachtPrices = (
  yacht: any,
  pricingOverrides: Array<ProposalYachtPricingOverride> | null,
) => {
  const { charterPrices = [], salePrices = [] } = yacht

  const combinedPrices: Array<YachtPricing> = [
    ...(charterPrices ?? []).reduce(mapSalesforceYachtCharterPrice, []),
    ...(salePrices ?? []).reduce(mapSalesforceYachtSalePrice, []),
    ...(pricingOverrides ?? []).map(mapOverrideYachtPrice),
  ]

  return combinedPrices
}

export const sortYachtPrices = (
  prices: Array<YachtPricing>,
  sortBy: string | null,
) => {
  switch (sortBy) {
    case 'price:desc':
      return prices.sort((prevPrice, nextPrice) => {
        const priceA =
          prevPrice.type === CHARTER_PRICING_TYPE
            ? prevPrice.amount.lowValue
            : prevPrice.amount.value
        const priceB =
          nextPrice.type === CHARTER_PRICING_TYPE
            ? nextPrice.amount.lowValue
            : nextPrice.amount.value
        return priceA && priceB ? priceB - priceA : 0
      })
    case 'price:asc':
    default:
      return prices.sort((prevPrice, nextPrice) => {
        const priceA =
          prevPrice.type === CHARTER_PRICING_TYPE
            ? prevPrice.amount.lowValue
            : prevPrice.amount.value
        const priceB =
          nextPrice.type === CHARTER_PRICING_TYPE
            ? nextPrice.amount.lowValue
            : nextPrice.amount.value
        return priceA && priceB ? priceA - priceB : 0
      })
  }
}

export const getAllPricesByType = (
  prices: Array<YachtPricing>,
  type: string,
) => {
  return prices.filter((price) => price.type === type)
}

export const parseYachtPrices = ({
  yacht,
  pricingOverrides,
  options = {
    sortBy: 'price:asc',
    byType: null,
  },
}: {
  yacht: any
  pricingOverrides: Array<ProposalYachtPricingOverride> | null
  options?: {
    sortBy: string | null
    byType: string | null
  }
}) => {
  if (!yacht) return []

  const { sortBy, byType } = options

  const combinedPrices: Array<YachtPricing> = combineYachtPrices(
    yacht,
    pricingOverrides,
  )

  const sortedPrices = sortYachtPrices(combinedPrices, sortBy)

  if (byType) {
    return getAllPricesByType(sortedPrices, byType)
  }

  return sortedPrices
}

export const getYachtImageFromProposalPage = (
  page: ProposalYachtPageDetails,
): ImageType | null => {
  const { hero } = page

  if (!hero) return null

  const { fallbackMedia, media } = hero

  const parsedMedia = media ? parseMediaObject(media) : null
  const parsedFallbackMedia = fallbackMedia
    ? parseMediaObject(fallbackMedia)
    : null

  if (parsedMedia && parsedMedia.isValidImage) return parsedMedia

  if (parsedFallbackMedia && parsedFallbackMedia.isValidImage)
    return parsedFallbackMedia

  return null
}

export const getYachtImageFromYacht = (
  yacht: ProposalYacht,
): { cardImage: ImageType | null; cardHoverImage: ImageType | null } => {
  const { cardMedia, cardMediaHover } = yacht.yacht

  const parsedCardMedia = cardMedia ? parseMediaObject(cardMedia) : null
  const parsedCardMediaHover = cardMediaHover
    ? parseMediaObject(cardMediaHover)
    : null

  return {
    cardImage:
      parsedCardMedia ?? parseMediaObject(PLACEHOLDER_YACHT_CARD_IMAGE),
    cardHoverImage: parsedCardMediaHover,
  }
}

export const stringOrNumberIsPositive = (
  value: string | number | unknown,
): boolean => {
  if (typeof value === 'number' && value > 0) return true
  if (typeof value === 'string' && value.length > 0) return true
  return false
}

export const getKeyYachtAmenitiesFromProposalPage = (
  proposalYacht: ProposalYacht,
  purchaseType: PurchaseType,
  displayYachtCrewCount: boolean,
): Array<string> | null => {
  const { yacht, specs } = proposalYacht

  if (!yacht) return null

  let keyYachtAmenitiesMap: {
    [index: string]: {
      default: string
      overrideKey: string
      // eslint-disable-next-line no-unused-vars
      isValidSpec: (value: string) => boolean
      // eslint-disable-next-line no-unused-vars
      shouldInclude: (value: string) => boolean
      // eslint-disable-next-line no-unused-vars
      specStringBuilder: (value: string) => string
    }
  } = {
    length: {
      default: yacht.lengthMetres,
      overrideKey: 'length',
      isValidSpec: stringOrNumberIsPositive,
      specStringBuilder: (value) => `${value}M`,
      shouldInclude: () => true,
    },
    guests: {
      default: yacht.guests,
      overrideKey: 'guests',
      isValidSpec: stringOrNumberIsPositive,
      specStringBuilder: (value) => `${value} Guests`,
      shouldInclude: () => purchaseType === 'charter' && !yacht.guestsSleeping,
    },
    guestsSleeping: {
      default: yacht.guestsSleeping,
      overrideKey: 'guests',
      isValidSpec: stringOrNumberIsPositive,
      specStringBuilder: (value) => `${value} Guests`,
      shouldInclude: () => purchaseType === 'charter',
    },
    crew: {
      default: yacht.crew,
      overrideKey: 'crew',
      isValidSpec: stringOrNumberIsPositive,
      specStringBuilder: (value) => `${value} Crew`,
      shouldInclude: () => displayYachtCrewCount,
    },
  }

  const amenityKeys = Object.keys(keyYachtAmenitiesMap)
  let amenities = []

  for (let i = 0; i < amenityKeys.length; i++) {
    const amenityKey = amenityKeys[i]
    const {
      specStringBuilder,
      default: defaultAmenity,
      overrideKey,
      isValidSpec,
      shouldInclude,
    } = keyYachtAmenitiesMap[amenityKey]

    if (!shouldInclude(defaultAmenity)) continue

    const foundSpecOverride = specs?.specOverrides
      ? specs?.specOverrides.find(
          (overrideSpec) => overrideSpec.specKey === overrideKey,
        )
      : null

    if (
      foundSpecOverride &&
      specs?.overrideYachtSpecs &&
      isValidSpec(foundSpecOverride.specValue)
    ) {
      amenities.push(specStringBuilder(foundSpecOverride.specValue))
      continue
    }

    const foundSpecAdditions = specs?.additionalYachtSpecs
      ? specs?.specAdditions?.find(
          (additionalSpec) =>
            additionalSpec.specAdditionLabel.toLowerCase() === overrideKey,
        )
      : null

    if (
      foundSpecAdditions &&
      isValidSpec(foundSpecAdditions.specAdditionLabel)
    ) {
      amenities.push(specStringBuilder(foundSpecAdditions.specAdditionValue))
      continue
    }

    if (!isValidSpec(defaultAmenity)) continue

    amenities.push(specStringBuilder(defaultAmenity))
  }

  return amenities
}

export const getYachtCardPriceValue = (
  yachtPrice: YachtPricing | null,
  {
    purchaseType,
    useHighRateAsDefault,
  }: {
    purchaseType: PurchaseType
    useHighRateAsDefault: boolean
  },
) => {
  if (!yachtPrice) return null

  if (
    (purchaseType === 'charter' || purchaseType === 'mixed') &&
    useHighRateAsDefault
  ) {
    return yachtPrice.amount?.highValue || yachtPrice.amount?.value
  }

  return yachtPrice.amount?.value
}

export const getYachtCardDisplayPrice = (yachtPrices: YachtPricing[]) => {
  if (!yachtPrices || !yachtPrices.length) return null

  const seasonOrderList = getSeasonOrderList()
  const selectedCardDisplayPrice = yachtPrices.find(
    (yachtPrice) => yachtPrice.isCardDisplayPrice,
  )

  const sortedYachtPrices = yachtPrices.sort((a, b) => {
    const indexA = seasonOrderList.indexOf(a.season?.toLowerCase() as any)
    const indexB = seasonOrderList.indexOf(b.season?.toLowerCase() as any)

    return (
      (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
    )
  })

  return selectedCardDisplayPrice || sortedYachtPrices[0]
}

export const buildPriceFrequencyStringFromFrequency = (
  isCharter: boolean | null | undefined,
  frequency: PriceFrequency | null | undefined,
): string => {
  if (frequency === 'purchase' || (!frequency && !isCharter)) return ''
  if (!frequency || (!frequency && isCharter)) return 'p/w'
  if (frequency === 'm') return 'p/m'
  if (frequency === 'd') return 'p/d'
  return 'p/w'
}

export const buildAvailabilityString = (
  availability: TimeframeType | null | undefined,
) => {
  if (!availability) return null

  const { from, until, year } = availability

  if (from && until)
    return `Availability: ${getDMYDate(from)} - ${getDMYDate(until)}`
  if (from && !until) return `Available from: ${getDMYDate(from)}`
  if (!from && until) return `Available until: ${getDMYDate(until)}`
  if (!from && !until && year) return `Available: ${year}`
  return null
}

export const isValidPriceString = (priceString: string | null | undefined) => {
  if (!priceString || priceString.charAt(0) === '-') return false

  const justNumbersArray = priceString.match(/(?<!-)\d+/g) // Only positive numbers within string
  const justNumbers = justNumbersArray ? justNumbersArray.join('') : null

  if (!justNumbers) return false

  const parsedPrice = parseFloat(justNumbers)

  return parsedPrice > 0
}
